/**
 * LangSync - 把 URL 的 :lang 同步到 i18next + <html lang> + localStorage。
 *
 * 作为 /:lang 路由的 layout 元素，渲染 <Outlet />。
 * URL 是语言唯一真相：进入任何 /:lang/* 路由时副作用同步三处。
 *
 * 不支持的语言（如 /fr/...）→ 重定向到默认语言，并保留 URL 后缀
 * （/fr/pricing → /zh/pricing，不丢路径）。
 */

import { useEffect, useRef } from "react";
import { Outlet, useParams, Navigate, useLocation } from "react-router-dom";
import i18n, { DEFAULT_LANG } from "../i18n";
import { isSupportedLang, rememberLang, stripLang } from "../lib/lang";
import Seo from "./Seo";

export default function LangSync() {
  const { lang } = useParams();
  const location = useLocation();
  const supported = isSupportedLang(lang);

  // 副作用必须无条件执行（Hook 顺序稳定）。
  // 仅在 lang 合法时同步；非法时交给下方的 <Navigate> 重定向。
  useEffect(() => {
    if (!supported) return;
    // 1. 切 i18next（触发所有 useTranslation 重渲染）
    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
    // 2. <html lang>（a11y / SEO）
    document.documentElement.lang = lang;
    // 3. 记住偏好（下次 / 重定向用）
    rememberLang(lang);
  }, [lang, supported]);

  // 上一次 location：用于识别「同页切语言」（stripLang 后 path、search、hash
  // 全部相同）。每次 location 变化都更新，保证判定用的是紧邻的上一次导航。
  const prevLocRef = useRef<{ pathname: string; search: string; hash: string } | null>(
    null,
  );
  // 带 hash 进入 /:lang 页（如从其他页顶栏「博客」跳回首页 #blog-section）：
  // SPA 导航浏览器不会自动滚动，此处补齐。首次进入时目标区块可能尚未挂载
  // （如博客精选要等数据加载），延迟重试一次。
  //
  // 例外——同页切语言（如 /zh#pricing → /en#pricing）：URL 的 hash 只是
  // LocaleSwitcher 原样带过来的残留，不代表锚点导航意图；用户此刻在哪个位置
  // 就留在哪里，不得滚向 hash 目标。
  useEffect(() => {
    const prevLoc = prevLocRef.current;
    prevLocRef.current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    };
    if (!location.hash || !supported) return;
    const pureLangSwitch =
      prevLoc !== null &&
      stripLang(prevLoc.pathname) === stripLang(location.pathname) &&
      prevLoc.search === location.search &&
      prevLoc.hash === location.hash;
    if (pureLangSwitch) return;
    const scroll = () => {
      document
        .getElementById(location.hash.slice(1))
        ?.scrollIntoView({ behavior: "smooth" });
    };
    scroll();
    const timer = setTimeout(scroll, 600);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search, location.hash, supported]);

  // 不支持的语言 → 重定向到默认语言，保留 path 后缀 + search + hash
  if (!supported) {
    // 去掉首段（旧 lang），拼上默认 lang。如 /fr/workbench/generate → /zh/workbench/generate
    const suffix = location.pathname.replace(/^\/[^/]+/, "");
    const target = `/${DEFAULT_LANG}${suffix || ""}${location.search}${location.hash}`;
    return <Navigate to={target} replace />;
  }

  return (
    <>
      {/* 运行时 SEO meta 注入（title/description/hreflang/canonical）。
          仅对 ROUTE_META 命中的公开页生效；登录页不注入。 */}
      <Seo />
      <Outlet />
    </>
  );
}
